import {
  ContactFiledInterface,
  ContactSlugEnum,
  CountryEnum,
  FormInput,
  FormInputPhone,
  FormSelect,
  Tooltip,
  getContactFieldTitle,
  getContactSlugName,
  getCountryData,
} from '@systemeio/ui'
import { deleteField } from 'modules/contacts/contact-page/api/contact-edit-api'
import { ContactFieldApiInterface } from 'modules/contacts/types/contact-filter-interface'
import { useState } from 'react'
import ConfirmationModal from 'shared/components/confirmation-modal'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import { KeyedMutator } from 'swr'
import { BinIcon } from '@systemeio/ui'

interface ContactEditFieldProps {
  field: ContactFiledInterface
  value: string | undefined
  onChange: (value?: string) => void
  className?: string
  mutateFields?: KeyedMutator<ContactFieldApiInterface>
  type: 'create' | 'edit'
  error?: string
}

const ContactFields = ({
  field,
  onChange,
  className,
  value,
  mutateFields,
  type,
  error,
}: ContactEditFieldProps) => {
  const { t } = useLocoTranslation()
  const { user } = useUser()
  const [opened, setOpened] = useState(false)

  const contactFieldTitle = getContactFieldTitle(field)

  const onDeleteField = async (id: number) => {
    if (typeof mutateFields === 'undefined') return

    await deleteField(id)
    await mutateFields(data => {
      if (data) {
        return { ...data, items: [...data.items.filter(el => el.id !== id)] }
      }
    }, false)
  }

  switch (field.slug) {
    case ContactSlugEnum.Country: {
      return (
        <FormSelect
          containerClassName={className}
          label={getContactSlugName(ContactSlugEnum.Country)}
          placeholder={getContactSlugName(ContactSlugEnum.Country)}
          data={getCountryData(user?.dashboardLocale)}
          value={value as CountryEnum}
          onChange={onChange}
          error={error}
        />
      )
    }
    case ContactSlugEnum.PhoneNumber: {
      return (
        <FormInputPhone
          wrapperClassName={className}
          label={getContactSlugName(ContactSlugEnum.PhoneNumber)}
          placeholder={getContactSlugName(ContactSlugEnum.PhoneNumber)}
          value={value}
          onChange={onChange}
          error={error}
        />
      )
    }
    default: {
      return (
        <FormInput
          required={field.slug === ContactSlugEnum.Email && type === 'create'}
          disabled={field.slug === ContactSlugEnum.Email && type === 'edit'}
          className={className}
          value={value}
          error={error}
          onChange={e => onChange(e.target.value)}
          labelClassName={field.slug !== ContactSlugEnum.Email ? 'w-full' : ''}
          nativeLabelProps={{ htmlFor: 'none' }}
          label={
            Object.values(ContactSlugEnum).includes(field.slug as ContactSlugEnum) ? (
              contactFieldTitle
            ) : (
              <div className={'flex w-full justify-between'}>
                {contactFieldTitle}
                <Tooltip mode={'hover'} label={t('dashboard.contact_page.delete_contact_field')}>
                  <button onClick={() => setOpened(true)} className={'group outline-hidden'}>
                    <BinIcon
                      className={
                        'main-transition-colors group-hover:fill-danger group-focus-visible:fill-danger h-[20px] w-[20px]'
                      }
                    />
                  </button>
                </Tooltip>
                <ConfirmationModal
                  confirmationText={t('dashboard.contact_page.delete_contact_field_description')}
                  toastCaption={t('dashboard.contact_page.field_removed')}
                  opened={opened}
                  onClose={() => setOpened(false)}
                  onConfirm={() => onDeleteField(field.id)}
                />
              </div>
            )
          }
          placeholder={contactFieldTitle}
        />
      )
    }
  }
}

export default ContactFields
